import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { PrimaryButton } from '../../components/PrimaryButton';
import logCherry from '../../../assets/logo-cherry-color.webp';
import {
  getMe,
  getMyPendingChange,
  getProConnectStatus,
  listProBookings,
  listProPayouts,
  type MyPendingProfileChange,
  type ProBookingRow,
  type ProConnectStatus,
  type ProPayoutRow,
} from '../../../lib/professional-api';
import { useAuth } from '../../../lib/use-auth';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatDuration(mins: number): string {
  if (!mins || mins < 1) return '';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const rest = mins % 60;
  return rest === 0 ? `${h}h` : `${h}h ${rest}m`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string>('');
  // Whether the pro has passed identity verification. Drives the
  // empty-bookings copy — a verified pro who simply has nothing upcoming
  // shouldn't be told "customers can book you once your identity is
  // verified" (they already are verified, and may even have completed
  // past bookings).
  const [verified, setVerified] = useState(false);
  // Stripe Connect readiness so the dashboard can prompt pros who
  // haven't finished payouts setup (or who fell out of compliance,
  // e.g. Stripe flagged a new requirement). Failure is non-fatal —
  // the dashboard still renders, the banner just stays hidden.
  const [connectStatus, setConnectStatus] = useState<ProConnectStatus | null>(
    null,
  );
  const [bookings, setBookings] = useState<ProBookingRow[]>([]);
  const [payouts, setPayouts] = useState<ProPayoutRow[]>([]);
  // Outstanding profile/service edit awaiting admin approval (treatments,
  // identity, etc.). Surfaced as a banner so the pro knows a change they
  // submitted is still pending — not silently lost.
  const [pendingChange, setPendingChange] =
    useState<MyPendingProfileChange | null>(null);
  // Whether the pro has provided their HMRC UTR. Default true so the banner
  // doesn't flash before /me loads; flipped to the real value on load.
  const [hasTaxUtr, setHasTaxUtr] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getMe(),
      listProBookings({ limit: 100 }),
      listProPayouts({ limit: 200 }),
    ])
      .then(([me, b, p]) => {
        if (cancelled) return;
        // Use the presigned preview — the canonical profile_pic points at
        // a private bucket and won't load in an <img>.
        setProfilePic(
          me?.info?.user?.profile_pic_preview_url ??
            me?.info?.user?.profile_pic ??
            null,
        );
        setBusinessName(me?.info?.business_name ?? '');
        setVerified(me?.info?.verification_status === 'VERIFIED');
        setHasTaxUtr(me?.info?.has_tax_utr !== false);
        setBookings(b.data ?? []);
        setPayouts(p.data ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = apiErrorMessage(err, 'Could not load dashboard.');
          toast.error(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Fire the Stripe Connect status fetch separately so a slow
    // /stripe-connect/status doesn't delay the main dashboard render.
    // Errors are non-fatal — the banner just stays hidden.
    getProConnectStatus()
      .then((res) => {
        if (cancelled) return;
        setConnectStatus(res.info);
      })
      .catch(() => {
        /* swallow — no banner shown */
      });

    // Pending admin-review change (best-effort; banner just stays hidden
    // on failure).
    getMyPendingChange()
      .then((res) => {
        if (!cancelled) setPendingChange(res.data ?? null);
      })
      .catch(() => {
        /* swallow — no banner shown */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- Derived stats ----------
  // "This month" is the calendar month of the user's local clock —
  // matches the date filter label below.
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime();

  const monthlyPayouts = useMemo(() => {
    return payouts.filter((p) => {
      const dateStr = p.transferred_at ?? p.created_at;
      if (!dateStr) return false;
      const t = new Date(dateStr).getTime();
      return t >= monthStart && t < monthEnd;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payouts]);

  const currency = payouts[0]?.currency ?? 'GBP';

  // Gross = sum of gross_amount this month. Net = sum after platform
  // fee. Both pulled straight from the payout_transactions rows we
  // already load on /professional/payouts.
  const monthGross = useMemo(
    () =>
      monthlyPayouts.reduce(
        (acc, p) => acc + Number(p.gross_amount ?? 0),
        0,
      ),
    [monthlyPayouts],
  );
  const monthNet = useMemo(
    () =>
      monthlyPayouts.reduce(
        (acc, p) => acc + Number(p.net_amount ?? 0),
        0,
      ),
    [monthlyPayouts],
  );

  // Distinct customers across all known bookings — lifetime metric
  // since we don't yet have a "this month only" breakdown.
  const uniqueClients = useMemo(() => {
    const set = new Set<string>();
    for (const b of bookings) {
      if (b.customer?.id) set.add(b.customer.id);
    }
    return set.size;
  }, [bookings]);

  // Completed bookings — a better "activity" stat than the
  // legacy "Listings views" mock.
  const completedCount = useMemo(
    () => bookings.filter((b) => b.status === 'COMPLETED').length,
    [bookings],
  );

  // Upcoming = bookings awaiting the pro's accept/reject decision
  // (PENDING_PROFESSIONAL_CONFIRM) plus CONFIRMED + IN_PROGRESS work,
  // capped at a clean 5. New requests are the whole reason a pro opens
  // the dashboard, so they're pulled to the top regardless of their
  // scheduled date; everything else stays in scheduled_start asc order.
  // (The dedicated "Pending" tab on /professional/bookings is where the
  // pro actually accepts; this just makes sure a new request is visible
  // the moment they land on the dashboard instead of reading "No
  // upcoming bookings yet.")
  const upcomingBookings = useMemo(() => {
    const rank = (status: string) =>
      status === 'PENDING_PROFESSIONAL_CONFIRM' ? 0 : 1;
    return bookings
      .filter(
        (b) =>
          b.status === 'PENDING_PROFESSIONAL_CONFIRM' ||
          b.status === 'CONFIRMED' ||
          b.status === 'IN_PROGRESS',
      )
      .slice()
      .sort((a, b) => {
        const byRank = rank(a.status) - rank(b.status);
        if (byRank !== 0) return byRank;
        return (
          new Date(a.scheduled_start).getTime() -
          new Date(b.scheduled_start).getTime()
        );
      })
      .slice(0, 5);
  }, [bookings]);

  const pendingCount = useMemo(
    () =>
      bookings.filter(
        (b) => b.status === 'PENDING_PROFESSIONAL_CONFIRM',
      ).length,
    [bookings],
  );

  const monthRangeLabel = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(undefined, {
      month: 'long',
      year: 'numeric',
    });
    return fmt.format(now);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayName =
    businessName ||
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
    'Your dashboard';

  const stats = [
    {
      label: 'This Month',
      sub: 'Gross earnings',
      value: formatMoney(monthGross, currency),
      accent: true,
    },
    {
      label: 'Income',
      sub: 'After platform fees',
      value: formatMoney(monthNet, currency),
      accent: false,
    },
    {
      label: 'Clients',
      sub: 'Distinct customers booked',
      value: String(uniqueClients),
      accent: false,
    },
    {
      label: 'Completed',
      sub: 'Bookings delivered',
      value: String(completedCount),
      accent: false,
    },
  ];

  return (
    <div className="min-h-screen bg-[#fff] flex flex-col font-['DM_Sans',sans-serif]">
      <HeaderPro />

      <main className="flex-1 w-full max-w-[1140px] mx-auto px-4 py-6 md:py-12">
        {pendingChange ? (
          <div className="mb-6 rounded-[12px] border border-[#f3d6a0] bg-[#fff7e6] px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="material-icons text-[20px] text-[#9a6700] mt-0.5">
                schedule
              </span>
              <div>
                <p className="font-bold text-[14px] text-[#3a3335]">
                  Changes awaiting approval
                </p>
                <p className="text-[13px] text-[#7a6a4f]">
                  You have updates to your services / profile submitted for
                  review. They'll go live once our team approves them.
                </p>
              </div>
            </div>
            <Link
              to="/professional/services"
              className="shrink-0 inline-flex items-center justify-center px-4 h-[40px] rounded-[8px] border border-[#d8a93f] bg-white text-[#9a6700] font-bold text-[13px] hover:bg-[#fff3d6]"
            >
              Review
            </Link>
          </div>
        ) : null}
        <PayoutsReadyBanner status={connectStatus} />
        <TaxUtrBanner hasUtr={hasTaxUtr} />
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[28px] md:text-[24px]">
            Dashboard
          </h1>
          <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg border border-[#eee]">
            <span className="material-icons text-[18px] text-[#908e8e]">
              calendar_today
            </span>
            <span className="text-[14px] font-bold text-[#3a3335]">
              {monthRangeLabel}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Profile + stats */}
          <div className="lg:col-span-4 bg-white rounded-lg border border-[#eee] p-8">
            <div className="w-20 h-20 rounded-lg overflow-hidden mb-6 bg-[#f2eee2] flex items-center justify-center">
              {profilePic ? (
                <img
                  src={profilePic}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[28px]">
                  {(user?.first_name ?? user?.email ?? '?')
                    .slice(0, 1)
                    .toUpperCase()}
                </span>
              )}
            </div>
            <div className="mb-4">
              <p className="text-[#908e8e] text-[14px] mb-1">Hello,</p>
              <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[22px]">
                {displayName}
              </h2>
              <p className="text-[#908e8e] text-[13px] mt-2 leading-relaxed">
                Here's how your work is going this month.
              </p>
              <div className="h-[1px] bg-[#eee] w-full mt-6" />
            </div>

            <div className="space-y-6 mb-8">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="flex justify-between items-end pb-0 last:border-0"
                >
                  <div>
                    <p className="font-bold text-[#3a3335] text-[15px]">
                      {stat.label}
                    </p>
                    <p className="text-[#908e8e] text-[12px]">{stat.sub}</p>
                  </div>
                  <div className="text-right">
                    <p
                      className="font-['Playfair_Display',sans-serif] font-bold text-[16px]"
                      style={{ color: stat.accent ? '#8e2a2f' : '#3a3335' }}
                    >
                      {loading ? '—' : stat.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link to="/professional/earnings" className="block w-full">
              <PrimaryButton className="w-full py-4 text-[15px]">
                View earnings
              </PrimaryButton>
            </Link>
          </div>

          {/* Right Column: Announcement + bookings */}
          <div className="lg:col-span-8 space-y-8">
            {/* Cancellation policy link block (CMS-backed). */}
            <div className="relative bg-[#f2eee6] rounded-lg p-10 flex flex-col md:flex-row items-center justify-between overflow-hidden py-3">
              <div className="relative z-10 max-w-[320px] text-center md:text-left mb-6 md:mb-0">
                <h3 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[18px] leading-tight mb-4">
                  Review the cancellation policy
                </h3>
                <Link to="/cancellation-policy">
                  <PrimaryButton className="w-auto px-6 py-3 text-sm">
                    Read policy
                  </PrimaryButton>
                </Link>
              </div>
              <div className="relative z-10 flex flex-col items-center">
                <img
                  src={logCherry}
                  alt="Cherry Picked"
                  className="w-[250px] h-auto mb-2 opacity-100"
                />
              </div>
            </div>

            {/* Upcoming Bookings */}
            <div className="bg-white rounded-lg border border-[#eee] p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <h3 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[16px]">
                    Upcoming Bookings
                  </h3>
                  {pendingCount > 0 ? (
                    <span className="px-2 py-0.5 rounded-full bg-[#fff7e6] text-[#9a6700] border border-[#fde2a1] font-bold text-[11px]">
                      {pendingCount} new
                    </span>
                  ) : null}
                </div>
                {upcomingBookings.length > 0 ? (
                  <Link
                    to="/professional/bookings"
                    className="text-[12px] font-bold text-[#8e2a2f] hover:underline"
                  >
                    View all
                  </Link>
                ) : null}
              </div>

              {loading ? (
                <p className="text-sm text-[#908e8e]">Loading bookings…</p>
              ) : upcomingBookings.length === 0 ? (
                <p className="text-sm text-[#908e8e]">
                  {verified
                    ? 'No upcoming bookings yet. New bookings will show up here.'
                    : 'No upcoming bookings yet. Customers can book you once your identity is verified.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {upcomingBookings.map((booking) => (
                    <div
                      key={booking.id}
                      onClick={() =>
                        navigate(`/professional/bookings/${booking.id}`)
                      }
                      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-2 border-b border-[#f5f5f5] last:border-0 last:pb-0 cursor-pointer hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-[#3a3335] text-[14px]">
                            {booking.service?.name ?? 'Booking'}
                          </p>
                          {booking.status ===
                          'PENDING_PROFESSIONAL_CONFIRM' ? (
                            <span className="px-2 py-0.5 rounded-[6px] bg-[#fff7e6] text-[#9a6700] border border-[#fde2a1] font-bold text-[11px] whitespace-nowrap">
                              Awaiting your confirmation
                            </span>
                          ) : null}
                        </div>
                        <p className="text-[#908e8e] text-[14px] mt-1">
                          {formatDateTime(booking.scheduled_start)}
                        </p>
                      </div>
                      <div className="flex items-center gap-12 w-full sm:w-auto justify-between sm:justify-end">
                        <p className="font-bold text-[#3a3335] text-[16px]">
                          {formatMoney(
                            Number(booking.total_amount ?? 0),
                            booking.currency ?? 'GBP',
                          )}
                        </p>
                        <p className="text-[#908e8e] text-[14px] min-w-[30px] text-right">
                          {formatDuration(booking.duration_minutes)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/**
 * Surfaces the pro's Stripe Connect readiness as an actionable banner
 * at the top of the dashboard. Three states:
 *
 *  - status === null (status fetch failed / pro doesn't have a
 *    Connect row yet): render nothing. We can't tell — silent
 *    failure is better than a misleading banner.
 *  - payouts_ready === true: render nothing. Pro is good to go.
 *  - payouts_ready === false: render the banner with copy + CTA
 *    tailored to the specific reason (never started / pending KYC /
 *    Stripe wants more info / deauthorized).
 *
 * The booking-accept endpoint also gates on payouts_ready
 * (Booking.service:acceptByPro) so a pro who ignores this banner
 * still can't accept jobs — the banner is the friendly nudge ahead
 * of that 400 response.
 */
function PayoutsReadyBanner({ status }: { status: ProConnectStatus | null }) {
  if (!status) return null;
  if (status.payouts_ready) return null;

  // Distinct copy per state so the pro knows whether to start, finish
  // or fix something.
  const isNotStarted = status.stripe_account_status === 'NOT_STARTED';
  const hasOutstanding =
    (status.currently_due?.length ?? 0) > 0 ||
    (status.past_due?.length ?? 0) > 0;
  const isDeauthorized = status.disabled_reason === 'deauthorized';

  let heading: string;
  let body: string;
  if (isDeauthorized) {
    heading = 'Stripe is disconnected — reconnect to receive payouts';
    body =
      'Your Stripe Connect account was disconnected. Reconnect to start accepting bookings again.';
  } else if (isNotStarted) {
    heading = 'Set up payouts to start accepting bookings';
    body =
      'We use Stripe to pay you. Verify your identity and add your bank account on Stripe — takes about 5 minutes.';
  } else if (hasOutstanding) {
    heading = 'Stripe needs more info before payouts can start';
    body =
      'Resolve the outstanding items on Stripe to unlock booking acceptance.';
  } else {
    heading = 'Finish your payout setup';
    body =
      'Your Stripe Connect account is still pending. Tap below to continue on Stripe.';
  }

  return (
    <div
      className="mb-6 rounded-[12px] border border-[#f3d6a0] bg-[#fff7e6] px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
      role="alert"
    >
      <div className="flex-1 min-w-0">
        <p className="font-['DM_Sans',sans-serif] font-bold text-[#9a6700] text-[14px]">
          {heading}
        </p>
        <p className="font-['DM_Sans',sans-serif] text-[#9a6700]/80 text-[12px] mt-1">
          {body}
        </p>
      </div>
      <Link
        to="/professional/setup-payouts"
        className="shrink-0 inline-flex items-center justify-center px-4 h-[40px] rounded-[8px] bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[13px] hover:bg-[#7a2428]"
      >
        {isNotStarted ? 'Set up payouts' : 'Continue'}
      </Link>
    </div>
  );
}

/**
 * Prompts existing pros (and anyone who skipped it) to add their HMRC UTR.
 * Payouts are held server-side until it's on file, so this is the friendly
 * nudge. Hidden once a UTR is present.
 */
function TaxUtrBanner({ hasUtr }: { hasUtr: boolean }) {
  if (hasUtr) return null;
  return (
    <div
      className="mb-6 rounded-[12px] border border-[#f3d6a0] bg-[#fff7e6] px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
      role="alert"
    >
      <div className="flex-1 min-w-0">
        <p className="font-['DM_Sans',sans-serif] font-bold text-[#9a6700] text-[14px]">
          Add your UTR to keep receiving payouts
        </p>
        <p className="font-['DM_Sans',sans-serif] text-[#9a6700]/80 text-[12px] mt-1">
          We need your 10-digit HMRC Unique Taxpayer Reference for tax
          reporting. Payouts are held until it's on file. We store it securely
          and never show it publicly.
        </p>
      </div>
      <Link
        to="/professional/setup-payouts"
        className="shrink-0 inline-flex items-center justify-center px-4 h-[40px] rounded-[8px] bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[13px] hover:bg-[#7a2428]"
      >
        Add UTR
      </Link>
    </div>
  );
}
